import { Component } from '@theme/component';
import { fetchConfig, preloadImage } from '@theme/utilities';
import { ThemeEvents, CartAddEvent, CartErrorEvent, CartUpdateEvent, VariantUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';

export const ADD_TO_CART_TEXT_ANIMATION_DURATION = 2000;

// Error message display duration - gives users time to read the message
const ERROR_MESSAGE_DISPLAY_DURATION = 10000;

// Button re-enable delay after error - prevents rapid repeat attempts
const ERROR_BUTTON_REENABLE_DELAY = 1000;

// Success message display duration for screen readers
const SUCCESS_MESSAGE_DISPLAY_DURATION = 5000;

/**
 * The 3D box (assets/plu-fx.js, "@theme/plu-fx"), once loaded and prewarmed.
 * @type {{ isReady(): boolean, playBox(host: HTMLElement, options: { tossX: number, tossY: number, image?: string }): { stop(): void } | null } | null}
 */
let pluFx = null;
let pluFxRequested = false;

/** Loads the 3D box when the page is idle (the CSS box plays until it is ready). */
function requestFx() {
  if (pluFxRequested) return;
  pluFxRequested = true;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  const device = /** @type {any} */ (navigator);
  // data saver or a very small device: the CSS version is lighter
  if (device.connection?.saveData || (device.deviceMemory && device.deviceMemory < 2) || (navigator.hardwareConcurrency || 4) < 3) return;
  const load = () => {
    import('@theme/plu-fx')
      .then((module) => {
        pluFx = module;
        return module.prewarm();
      })
      .catch(() => {
        // no 3D: the CSS box stays
      });
  };
  // well after the page has loaded, in an idle moment, while the page is on screen
  const idle = () => {
    if (document.visibilityState !== 'visible') {
      document.addEventListener('visibilitychange', idle, { once: true });
      return;
    }
    if (typeof window.requestIdleCallback === 'function') window.requestIdleCallback(load, { timeout: 5000 });
    else window.setTimeout(load, 1500);
  };
  // only once the visitor starts using the page (scroll, touch, mouse or key),
  // so a quick visit never downloads it; well after the page has loaded
  const events = ['scroll', 'pointerdown', 'pointermove', 'touchstart', 'keydown'];
  const start = () => {
    events.forEach((name) => window.removeEventListener(name, start));
    if (document.readyState === 'complete') window.setTimeout(idle, 1000);
    else window.addEventListener('load', () => window.setTimeout(idle, 1000), { once: true });
  };
  events.forEach((name) => window.addEventListener(name, start, { once: true, passive: true }));
}

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];

  /** @type {number | undefined} */
  #animationTimeout;

  /** @type {number | undefined} */
  #cleanupTimeout;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('pointerenter', this.#preloadImage);
    requestFx();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);
    this.removeEventListener('pointerenter', this.#preloadImage);
  }

  /**
   * Disables the add to cart button.
   */
  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  /**
   * Enables the add to cart button.
   */
  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  /**
   * Handles the click event for the add to cart button.
   * @param {MouseEvent & {target: HTMLElement}} event - The click event.
   */
  handleClick(event) {
    const form = this.closest('form');
    if (!form?.checkValidity()) return;

    // Check if adding would exceed max before animating
    const quantitySelector = /** @type {any} */ (form.querySelector('quantity-selector-component'));
    if (quantitySelector?.canAddToCart) {
      const validation = quantitySelector.canAddToCart();
      // Don't animate if it would exceed max
      if (!validation.canAdd) {
        return;
      }
    }

    this.animateAddToCart();

    const animationEnabled = this.dataset.addToCartAnimation === 'true';

    if (animationEnabled && !event.target.closest('.quick-add-modal')) {
      this.#animateFlyToCart();
    }
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;

    if (!image) return;

    preloadImage(image);
  };

  /**
   * PLUPack: the product goes into a box that is sealed and jumps into the cart.
   */
  #animateFlyToCart() {
    const { addToCartButton } = this.refs;
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    if (!cartIcon || !addToCartButton) return;

    const quantityInput = this.closest('form')?.querySelector('input[name="quantity"]');
    const quantity = quantityInput instanceof HTMLInputElement ? Number(quantityInput.value) || 1 : 1;

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

    flyToCartElement.image = this.dataset.productVariantMedia ?? '';
    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = cartIcon;
    flyToCartElement.quantity = quantity;

    document.body.appendChild(flyToCartElement);
  }

  /**
   * Animates the add to cart button.
   */
  animateAddToCart() {
    const { addToCartButton } = this.refs;

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);

    if (!addToCartButton.classList.contains('atc-added')) {
      addToCartButton.classList.add('atc-added');
    }

    this.#animationTimeout = setTimeout(() => {
      this.#cleanupTimeout = setTimeout(() => {
        this.refs.addToCartButton.classList.remove('atc-added');
      }, 10);
    }, ADD_TO_CART_TEXT_ANIMATION_DURATION);
  }
}

if (!customElements.get('add-to-cart-component')) {
  customElements.define('add-to-cart-component', AddToCartComponent);
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement} liveRegion - The live region.
 * @property {HTMLElement | undefined} quantityLabelCartCount - The quantity label cart count element.
 * @property {HTMLElement | undefined} quantityRules - The quantity rules element.
 * @property {HTMLElement | undefined} productFormButtons - The product form buttons container.
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];
  #abortController = new AbortController();

  /** @type {number | undefined} */
  #timeout;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#onVariantSelected, { signal });

    // Listen for cart updates to sync data-cart-quantity
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }

  /**
   * Fetches cart and updates quantity selector for current variant
   * @returns {Promise<number>} The cart quantity for the current variant
   */
  async #fetchAndUpdateCartQuantity() {
    const variantIdInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'));
    if (!variantIdInput?.value) return 0;

    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      const cartItem = cart.items.find(
        /** @param {any} item */
        (item) => item.variant_id.toString() === variantIdInput.value.toString()
      );
      const cartQty = cartItem ? cartItem.quantity : 0;

      // Use public API to update quantity selector
      const quantitySelector = /** @type {any} */ (this.querySelector('quantity-selector-component'));
      if (quantitySelector?.setCartQuantity) {
        quantitySelector.setCartQuantity(cartQty);
      }

      // Update quantity label if it exists
      this.#updateQuantityLabel(cartQty);

      return cartQty;
    } catch (error) {
      console.error('Failed to fetch cart quantity:', error);
      return 0;
    }
  }

  /**
   * Updates data-cart-quantity when cart is updated from elsewhere
   * @param {CartUpdateEvent|CartAddEvent} event
   */
  #onCartUpdate = async (event) => {
    // Skip if this event came from this component
    if (event.detail?.sourceId === this.id || event.detail?.data?.source === 'product-form-component') return;

    await this.#fetchAndUpdateCartQuantity();
  };

  /**
   * Handles the submit event for the product form.
   *
   * @param {Event} event - The submit event.
   */
  handleSubmit(event) {
    const { addToCartTextError, addToCartButtonContainer } = this.refs;
    // Stop default behaviour from the browser
    event.preventDefault();

    if (this.#timeout) clearTimeout(this.#timeout);

    // Check if the add to cart button is disabled and do an early return if it is
    if (addToCartButtonContainer?.refs.addToCartButton?.disabled) return;

    // Send the add to cart information to the cart
    const form = this.querySelector('form');

    if (!form) throw new Error('Product form element missing');

    const quantitySelector = /** @type {any} */ (this.querySelector('quantity-selector-component'));
    if (quantitySelector?.canAddToCart) {
      const validation = quantitySelector.canAddToCart();

      if (!validation.canAdd) {
        addToCartButtonContainer?.disable();

        const errorTemplate = this.dataset.quantityErrorMax || '';
        const errorMessage = errorTemplate.replace('{{ maximum }}', validation.maxQuantity.toString());
        if (addToCartTextError) {
          addToCartTextError.classList.remove('hidden');

          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = errorMessage;
          } else {
            const newTextNode = document.createTextNode(errorMessage);
            addToCartTextError.appendChild(newTextNode);
          }

          this.#setLiveRegionText(errorMessage);

          if (this.#timeout) clearTimeout(this.#timeout);
          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);
        }

        setTimeout(() => {
          addToCartButtonContainer?.enable();
        }, ERROR_BUTTON_REENABLE_DELAY);

        return;
      }
    }

    const formData = new FormData(form);

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    let cartItemComponentsSectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
      formData.append('sections', cartItemComponentsSectionIds.join(','));
    });

    const fetchCfg = fetchConfig('javascript', { body: formData });

    fetch(Theme.routes.cart_add_url, {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        Accept: 'text/html',
      },
    })
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          this.dispatchEvent(
            new CartErrorEvent(form.getAttribute('id') || '', response.message, response.description, response.errors)
          );

          if (!addToCartTextError) return;
          addToCartTextError.classList.remove('hidden');

          // Reuse the text node if the user is spam-clicking
          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = response.message;
          } else {
            const newTextNode = document.createTextNode(response.message);
            addToCartTextError.appendChild(newTextNode);
          }

          // Create or get existing error live region for screen readers
          this.#setLiveRegionText(response.message);

          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');

            // Clear the announcement
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);

          // When we add more than the maximum amount of items to the cart, we need to dispatch a cart update event
          // because our back-end still adds the max allowed amount to the cart.
          this.dispatchEvent(
            new CartAddEvent({}, this.id, {
              didError: true,
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
            })
          );

          return;
        } else {
          const id = formData.get('id');

          if (addToCartTextError) {
            addToCartTextError.classList.add('hidden');
            addToCartTextError.removeAttribute('aria-live');
          }

          if (!id) throw new Error('Form ID is required');

          // Add aria-live region to inform screen readers that the item was added
          if (this.refs.addToCartButtonContainer?.refs.addToCartButton) {
            const addToCartButton = this.refs.addToCartButtonContainer.refs.addToCartButton;
            const addedTextElement = addToCartButton.querySelector('.add-to-cart-text--added');
            const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;

            this.#setLiveRegionText(addedText);

            setTimeout(() => {
              this.#clearLiveRegionText();
            }, SUCCESS_MESSAGE_DISPLAY_DURATION);
          }

          // Fetch the updated cart to get the actual total quantity for this variant
          this.#fetchAndUpdateCartQuantity();

          this.dispatchEvent(
            new CartAddEvent({}, id.toString(), {
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
              sections: response.sections,
            })
          );
        }
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        cartPerformance.measureFromEvent('add:user-action', event);
      });
  }

  /**
   * Updates the quantity label with the current cart quantity
   * @param {number} cartQty - The quantity in cart
   */
  #updateQuantityLabel(cartQty) {
    const quantityLabel = this.refs.quantityLabelCartCount;
    if (quantityLabel) {
      const inCartText = quantityLabel.textContent?.match(/\((\d+)\s+(.+)\)/);
      if (inCartText && inCartText[2]) {
        quantityLabel.textContent = `(${cartQty} ${inCartText[2]})`;
      }

      // Show/hide based on quantity
      quantityLabel.classList.toggle('hidden', cartQty === 0);
    }
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = '';
  }

  /**
   * Morphs or removes/adds an element based on current and new element states
   * @param {Element | null | undefined} currentElement - The current element in the DOM
   * @param {Element | null | undefined} newElement - The new element from the server response
   * @param {Element | null} [insertReferenceElement] - Element to insert before if adding new element
   */
  #morphOrUpdateElement(currentElement, newElement, insertReferenceElement = null) {
    if (currentElement && newElement) {
      morph(currentElement, newElement);
    } else if (currentElement && !newElement) {
      currentElement.remove();
    } else if (!currentElement && newElement && insertReferenceElement) {
      insertReferenceElement.insertAdjacentElement('beforebegin', /** @type {Element} */ (newElement.cloneNode(true)));
    }
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = async (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const { variantId, addToCartButtonContainer } = this.refs;

    const currentAddToCartButton = addToCartButtonContainer?.refs.addToCartButton;
    const newAddToCartButton = event.detail.data.html.querySelector('[ref="addToCartButton"]');

    // Update the variant ID
    variantId.value = event.detail.resource?.id ?? '';

    if (!currentAddToCartButton && !this.refs.acceleratedCheckoutButtonContainer) return;

    // Update the button state
    if (currentAddToCartButton) {
      if (event.detail.resource == null || event.detail.resource.available == false) {
        addToCartButtonContainer.disable();
      } else {
        addToCartButtonContainer.enable();
      }

      // Update the add to cart button text and icon
      if (newAddToCartButton) {
        morph(currentAddToCartButton, newAddToCartButton);
      }
    }

    if (this.refs.acceleratedCheckoutButtonContainer) {
      if (event.detail.resource == null || event.detail.resource.available == false) {
        this.refs.acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
      } else {
        this.refs.acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
      }
    }

    // Set the data attribute for the add to cart button to the product variant media if it exists
    if (event.detail.resource) {
      const productVariantMedia = event.detail.resource.featured_media?.preview_image?.src;
      productVariantMedia &&
        addToCartButtonContainer?.setAttribute('data-product-variant-media', productVariantMedia + '&width=100');
    }

    // Update quantity selector's min/max/step attributes and cart quantity for the new variant
    const quantitySelector = /** @type {any} */ (this.querySelector('quantity-selector-component'));
    const newQuantityInput = /** @type {HTMLInputElement | null} */ (
      event.detail.data.html.querySelector('quantity-selector-component input[ref="quantityInput"]')
    );

    if (quantitySelector?.updateConstraints && newQuantityInput) {
      quantitySelector.updateConstraints(
        newQuantityInput.min,
        newQuantityInput.max || null,
        newQuantityInput.step
      );
    }

    // Check if quantity rules are appearing/disappearing (causes layout shift)
    const quantityRules = this.refs.quantityRules;
    const newQuantityRules = event.detail.data.html.querySelector('.quantity-rules');
    const isQuantityRulesChanging = !!quantityRules !== !!newQuantityRules;

    if (isQuantityRulesChanging && quantitySelector) {
      // Store quantity value before morphing entire container
      const currentQuantityValue = quantitySelector.getValue?.();

      const currentProductFormButtons = this.refs.productFormButtons;
      const newProductFormButtons = event.detail.data.html.querySelector('.product-form-buttons');

      if (currentProductFormButtons && newProductFormButtons) {
        morph(currentProductFormButtons, newProductFormButtons);

        // Get the NEW quantity selector after morphing and update its constraints
        const newQuantitySelector = /** @type {any} */ (this.querySelector('quantity-selector-component'));
        const newQuantityInputElement = /** @type {HTMLInputElement | null} */ (
          event.detail.data.html.querySelector('quantity-selector-component input[ref="quantityInput"]')
        );

        if (newQuantitySelector?.updateConstraints && newQuantityInputElement && currentQuantityValue) {
          // Temporarily set the old value so updateConstraints can snap it properly
          newQuantitySelector.setValue(currentQuantityValue);
          // updateConstraints will snap to valid increment if needed
          newQuantitySelector.updateConstraints(
            newQuantityInputElement.min,
            newQuantityInputElement.max || null,
            newQuantityInputElement.step
          );
        }
      }
    } else {
      // Update elements individually when layout isn't changing
      const quantityLabel = this.querySelector('.quantity-label');
      const newQuantityLabel = event.detail.data.html.querySelector('.quantity-label');
      this.#morphOrUpdateElement(quantityLabel, newQuantityLabel, quantitySelector);

      const addToCartButton = this.querySelector('[ref="addToCartButtonContainer"]');
      this.#morphOrUpdateElement(quantityRules, newQuantityRules, addToCartButton);
    }

    // Fetch and update cart quantity for the new variant
    await this.#fetchAndUpdateCartQuantity();
  };

  /**
   * Disable the add to cart button while the UI is updating before #onVariantUpdate is called.
   * Accelerated checkout button is also disabled via its own event listener not exposed to the theme.
   */
  #onVariantSelected = () => {
    this.refs.addToCartButtonContainer?.disable();
  };
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}

// ============================================
// ADD TO CART: THE BOX (PLUPack)
// ============================================
// A kraft box pops up over the button and the product is tossed into it. The
// flaps close, a strip of PLUPack tape seals it and the box jumps into the
// cart icon, which catches it: the icon bounces and a "+N" tag shows the
// units added. The cart drawer waits for the landing before it opens
// (cart-drawer.js listens for `plu:fly-landed`). The box is drawn in 3D by
// assets/plu-fx.js once it has loaded (when the page is idle); until then, or
// without WebGL, the CSS box plays instead (its pieces and timeline live in
// assets/plu-shop.css). Either way this file flies it to the cart.

/** When the sealed box jumps toward the cart, in ms (the timeline in plu-shop.css is built around it). */
const BOX_TAKEOFF = 1080;
/** Length of the jump into the cart, in ms. */
const BOX_FLIGHT = 500;
/** Room between the button and the bottom of the box, in px. */
const BOX_GAP = 14;
/** From the center of the box to its lowest edge on screen, in px (see plu-shop.css). */
const BOX_BOTTOM = 40;
/** The box never gets closer than this to the edges of the screen, in px. */
const BOX_MARGIN = { side: 64, top: 150, bottom: 56 };
const BURST_COLORS = ['#8cc8f2', '#f2be00', '#ffffff'];

const BOX_MARKUP = `
  <div class="plu-box__shadow"></div>
  <div class="plu-box">
    <div class="plu-box__body">
      <div class="plu-box__side plu-box__side--back"><div class="plu-box__face"></div><div class="plu-box__flap"></div></div>
      <div class="plu-box__side plu-box__side--left"><div class="plu-box__face"></div><div class="plu-box__flap"></div></div>
      <div class="plu-box__bottom"></div>
      <div class="plu-box__item"></div>
      <div class="plu-box__side plu-box__side--right">
        <div class="plu-box__face"></div><div class="plu-box__flap"></div><div class="plu-box__tape-end"></div>
      </div>
      <div class="plu-box__side plu-box__side--front">
        <div class="plu-box__face"><span class="plu-box__brand">PLUPack</span></div><div class="plu-box__flap"></div>
      </div>
      <div class="plu-box__top"><div class="plu-box__tape"></div></div>
    </div>
  </div>
  <div class="plu-box__toss"></div>
`;

class FlyToCart extends HTMLElement {
  /** @type {Element} */
  source;

  /** @type {Element} */
  destination;

  /** Units added, shown on the "+N" tag. */
  quantity = 1;

  /** Picture of the product that goes into the box. */
  image = '';

  #landed = false;

  #fallback = 0;

  /** The 3D box playing in this element, if any. @type {{ stop(): void } | null} */
  #gl = null;

  connectedCallback() {
    this.classList.add('plu-fly');
    this.setAttribute('aria-hidden', 'true');

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const target = this.destination?.getBoundingClientRect();
    if (reducedMotion || !this.source || !target || target.width === 0 || typeof this.animate !== 'function') {
      this.#land(false);
      this.remove();
      return;
    }

    if (!this.#build3d()) this.#build();
  }

  disconnectedCallback() {
    window.clearTimeout(this.#fallback);
    this.#gl?.stop();
    this.#gl = null;
    // removed before landing: don't keep the cart drawer waiting
    if (!this.#landed) this.#land(false);
  }

  /**
   * Where the box appears (over the button, always inside the screen), where
   * the product is tossed from and where the box lands (the cart icon, or the
   * top of the screen when the header is out of view).
   */
  #path() {
    const button = this.source.getBoundingClientRect();
    const cart = this.destination.getBoundingClientRect();
    const start = {
      x: clamp(button.left + button.width / 2, BOX_MARGIN.side, window.innerWidth - BOX_MARGIN.side),
      y: clamp(button.top - BOX_GAP - BOX_BOTTOM, BOX_MARGIN.top, window.innerHeight - BOX_MARGIN.bottom),
    };
    const toss = {
      x: button.left + button.width / 2 - start.x,
      y: button.top + button.height / 2 - start.y,
    };
    const end = {
      x: cart.left + cart.width / 2,
      y: cart.bottom > 0 ? cart.top + cart.height / 2 : 12,
    };
    this.style.setProperty('--plu-box-x', `${start.x.toFixed(1)}px`);
    this.style.setProperty('--plu-box-y', `${start.y.toFixed(1)}px`);
    this.style.setProperty('--plu-toss-x', `${toss.x.toFixed(1)}px`);
    this.style.setProperty('--plu-toss-y', `${toss.y.toFixed(1)}px`);
    return { start, toss, end };
  }

  /** The 3D box (assets/plu-fx.js), when it has loaded. */
  #build3d() {
    if (!pluFx?.isReady()) return false;
    const { start, toss, end } = this.#path();
    this.classList.add('plu-fly--gl');
    let handle = null;
    try {
      handle = pluFx.playBox(this, { tossX: toss.x, tossY: toss.y, image: this.image });
    } catch {
      handle = null;
    }
    if (!handle) {
      this.classList.remove('plu-fly--gl');
      return false;
    }
    this.#gl = handle;
    this.#fly(start, end);
    return true;
  }

  /** The CSS box (assets/plu-shop.css). */
  #build() {
    this.innerHTML = BOX_MARKUP;
    if (this.image) {
      const picture = `url(${JSON.stringify(this.image)})`;
      for (const card of this.querySelectorAll('.plu-box__item, .plu-box__toss')) {
        if (card instanceof HTMLElement) card.style.backgroundImage = picture;
      }
    } else {
      this.classList.add('plu-fly--empty');
    }
    const { start, end } = this.#path();
    this.#fly(start, end);
  }

  /**
   * The sealed box jumps along an arc into the cart.
   * @param {{x: number, y: number}} start
   * @param {{x: number, y: number}} end
   */
  #fly(start, end) {
    const lift = Math.min(200, Math.max(110, Math.abs(end.y - start.y) * 0.3 + 90));
    const control1 = { x: start.x + (end.x - start.x) * 0.2, y: start.y - lift };
    // the arc may rise above the cart, but never off the top of the screen
    const control2 = { x: end.x - (end.x - start.x) * 0.08, y: Math.max(Math.min(start.y, end.y) - lift * 0.55, 8) };
    const tilt = end.x >= start.x ? 1 : -1;

    const steps = 32;
    /** @type {Keyframe[]} */
    const frames = [];
    for (let step = 0; step <= steps; step++) {
      const t = step / steps;
      const point = bezierPoint(t, start, control1, control2, end);
      const scale = 1 - 0.7 * Math.pow(t, 1.6);
      const rotation = tilt * 16 * Math.sin(t * Math.PI);
      const opacity = t > 0.84 ? Math.max(0, 1 - (t - 0.84) / 0.16) : 1;
      frames.push({
        offset: t,
        transform: `translate(${point.x.toFixed(1)}px, ${point.y.toFixed(1)}px) rotate(${rotation.toFixed(2)}deg) scale(${scale.toFixed(3)})`,
        opacity,
      });
    }

    const flight = this.animate(frames, { duration: BOX_FLIGHT, delay: BOX_TAKEOFF, easing: 'linear', fill: 'both' });
    flight.finished.then(
      () => this.#finish(),
      () => this.#finish()
    );
    // in case the animation never gets to finish
    this.#fallback = window.setTimeout(() => this.#finish(), BOX_TAKEOFF + BOX_FLIGHT + 700);
  }

  #finish() {
    window.clearTimeout(this.#fallback);
    this.#land(true);
    this.remove();
  }

  /**
   * The cart catches the box.
   * @param {boolean} burst - Bounce the icon and show the sparks and the "+N" tag
   */
  #land(burst) {
    if (this.#landed) return;
    this.#landed = true;
    this.setAttribute('data-landed', '');
    document.dispatchEvent(new CustomEvent('plu:fly-landed'));

    const icon = this.destination;
    if (!(icon instanceof HTMLElement) || !burst) return;

    if (getComputedStyle(icon).display === 'inline') icon.style.display = 'inline-block';
    icon.classList.remove('plu-cart-catch');
    void icon.offsetWidth;
    icon.classList.add('plu-cart-catch');
    icon.addEventListener('animationend', () => icon.classList.remove('plu-cart-catch'), { once: true });
    showBurst(icon, this.quantity);

    try {
      if (window.matchMedia('(pointer: coarse)').matches) navigator.vibrate?.(14);
    } catch {
      // vibration is optional
    }
  }
}

/**
 * A ring, a few sparks and a "+N" tag around the cart icon.
 * @param {HTMLElement} icon
 * @param {number} quantity
 */
function showBurst(icon, quantity) {
  const rect = icon.getBoundingClientRect();
  const burst = document.createElement('div');
  burst.className = 'plu-burst';
  burst.setAttribute('aria-hidden', 'true');
  burst.style.transform = `translate(${(rect.left + rect.width / 2).toFixed(1)}px, ${(rect.top + rect.height / 2).toFixed(1)}px)`;

  const ring = document.createElement('span');
  ring.className = 'plu-burst__ring';
  burst.appendChild(ring);

  for (let index = 0; index < 8; index++) {
    const angle = (index / 8) * Math.PI * 2 + Math.PI / 8;
    const distance = 26 + (index % 2) * 10;
    const dot = document.createElement('span');
    dot.className = 'plu-burst__dot';
    dot.style.setProperty('--plu-dx', `${(Math.cos(angle) * distance).toFixed(1)}px`);
    dot.style.setProperty('--plu-dy', `${(Math.sin(angle) * distance).toFixed(1)}px`);
    dot.style.setProperty('--plu-dot', BURST_COLORS[index % BURST_COLORS.length] ?? '#8cc8f2');
    burst.appendChild(dot);
  }

  const tag = document.createElement('span');
  tag.className = 'plu-burst__plus';
  tag.textContent = `+${quantity}`;
  burst.appendChild(tag);

  document.body.appendChild(burst);
  window.setTimeout(() => burst.remove(), 1200);
}

/**
 * @param {number} value
 * @param {number} min
 * @param {number} max
 */
function clamp(value, min, max) {
  return Math.min(Math.max(value, min), Math.max(min, max));
}

/**
 * Calculates a point on a cubic Bézier curve.
 * @param {number} t - The parameter value (0 <= t <= 1).
 * @param {{x: number, y: number}} p0 - The starting point (x, y).
 * @param {{x: number, y: number}} p1 - The first control point (x, y).
 * @param {{x: number, y: number}} p2 - The second control point (x, y).
 * @param {{x: number, y: number}} p3 - The ending point (x, y).
 * @returns {{x: number, y: number}} The point on the curve.
 */
function bezierPoint(t, p0, p1, p2, p3) {
  const cX = 3 * (p1.x - p0.x);
  const bX = 3 * (p2.x - p1.x) - cX;
  const aX = p3.x - p0.x - cX - bX;

  const cY = 3 * (p1.y - p0.y);
  const bY = 3 * (p2.y - p1.y) - cY;
  const aY = p3.y - p0.y - cY - bY;

  const x = aX * Math.pow(t, 3) + bX * Math.pow(t, 2) + cX * t + p0.x;
  const y = aY * Math.pow(t, 3) + bY * Math.pow(t, 2) + cY * t + p0.y;

  return { x, y };
}

if (!customElements.get('fly-to-cart')) {
  customElements.define('fly-to-cart', FlyToCart);
}
